import { useCustomerAuth } from "@/components/customer/CustomerAuthProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Heart, Package, Clock, MapPin, Star, CheckCircle, MessageSquare, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLocalization } from "@/hooks/useLocalization";

export default function CustomerDashboard() {
  const { customer } = useCustomerAuth();
  const { t, language } = useLocalization();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Update current time every second for real-time countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch recent activity
  useEffect(() => {
    const fetchRecentActivity = async () => {
      if (!customer?.id) return;
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        // Fetch recent appointments and orders
        const [appointmentsResponse, ordersResponse] = await Promise.all([
          supabase.functions.invoke('customer-appointments', {
            headers: { Authorization: `Bearer ${session.access_token}` }
          }),
          supabase.functions.invoke('customer-orders-list', {
            body: { 
              customer_email: customer.email,
              session_token: session?.access_token || 'customer_portal_session'
            }
          })
        ]);

        const activities = [];

        // Add recent appointments
        if (appointmentsResponse.data?.appointments) {
          const recentAppointments = appointmentsResponse.data.appointments
            .slice(0, 2)
            .map((apt: any) => ({
              type: 'appointment',
              title: apt.title,
              description: `${apt.type.charAt(0).toUpperCase() + apt.type.slice(1)} appointment`,
              date: apt.start_time,
              icon: Calendar
            }));
          activities.push(...recentAppointments);
        }

        // Add recent orders
        if (ordersResponse.data?.orders) {
          const recentOrders = ordersResponse.data.orders
            .slice(0, 2)
            .map((order: any) => ({
              type: 'order',
              title: `Order ${order.id}`,
              description: `Status: ${order.status.charAt(0).toUpperCase() + order.status.slice(1)}`,
              date: order.updated,
              icon: Package
            }));
          activities.push(...recentOrders);
        }

        // Sort by date (most recent first)
        activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        
        setRecentActivity(activities.slice(0, 3));
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentActivity();
  }, [customer?.id]);

  const getWeddingCountdown = () => {
    if (!customer?.wedding_date) {
      return null;
    }
    
    const weddingDate = new Date(customer.wedding_date);
    
    if (isNaN(weddingDate.getTime())) {
      return null;
    }
    
    const timeDiff = weddingDate.getTime() - currentTime.getTime();
    
    if (timeDiff <= 0) {
      return { isPast: true };
    }
    
    const days = Math.floor(timeDiff / (1000 * 3600 * 24));
    const hours = Math.floor((timeDiff % (1000 * 3600 * 24)) / (1000 * 3600));
    const minutes = Math.floor((timeDiff % (1000 * 3600)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds, isPast: false };
  };

  const countdown = getWeddingCountdown();

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Welcome Header */}
      <div className="text-center space-y-2 px-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
          {t('welcome_back')}, {customer?.first_name}!
        </h1>
        <p className="text-muted-foreground text-base leading-relaxed">
          {t('dream_dress_journey')}
        </p>
      </div>

      {/* Wedding Countdown */}
      {countdown && (
        <Card className="bg-gradient-subtle border-primary/20">
          <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6">
            {countdown.isPast ? (
              <div className="text-center space-y-2">
                <div className="text-2xl sm:text-3xl font-bold text-primary">
                  🎉 {t('wedding_passed')} 🎉
                </div>
                <div className="text-base sm:text-lg text-muted-foreground">
                  {t('congratulations_wedding')}
                </div>
              </div>
            ) : (
              <div className="text-center space-y-3">
                <h2 className="text-lg sm:text-xl font-semibold text-primary mb-4">
                  {t('wedding_countdown')} ✨
                </h2>
                
                {/* Countdown Display */}
                <div 
                  className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4"
                  role="timer"
                  aria-label={`Wedding countdown: ${countdown.days} days, ${countdown.hours} hours, ${countdown.minutes} minutes, ${countdown.seconds} seconds remaining`}
                >
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary">
                      {countdown.days}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      {countdown.days === 1 ? t('day') : t('days')}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary">
                      {countdown.hours.toString().padStart(2, '0')}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      {countdown.hours === 1 ? t('hour') : t('hours')}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary">
                      {countdown.minutes.toString().padStart(2, '0')}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      {countdown.minutes === 1 ? t('minute') : t('minutes')}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary">
                      {countdown.seconds.toString().padStart(2, '0')}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      {countdown.seconds === 1 ? t('second') : t('seconds')}
                    </div>
                  </div>
                </div>
                
                <div className="text-base sm:text-lg text-muted-foreground">
                  {t('days_until_wedding')}
                </div>
                
                {/* Wedding Date */}
                <div className="text-xs sm:text-sm text-muted-foreground px-2">
                  {new Date(customer.wedding_date!).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
                
                {/* Accessibility fallback for screen readers */}
                <div className="sr-only" aria-live="polite" aria-atomic="true">
                  {countdown.days} {t('countdown_sr_days')}, {countdown.hours} {t('countdown_sr_hours')}, {countdown.minutes} {t('countdown_sr_minutes')}, {countdown.seconds} {t('countdown_sr_seconds')} {t('countdown_sr_until')}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
        <Card className="hover:shadow-md transition-all duration-200 cursor-pointer group">
          <Link 
            to="/appointments"
            className="block h-full focus:outline-none focus:ring-2 focus:ring-primary rounded-lg touch-manipulation"
            aria-label={t('appointments')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Calendar className="h-7 w-7 sm:h-8 sm:w-8 text-primary group-hover:scale-110 transition-transform" aria-hidden="true" />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <CardTitle className="text-base sm:text-lg mb-1 sm:mb-2">{t('appointments')}</CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {t('view_manage_fittings')}
              </CardDescription>
            </CardContent>
          </Link>
        </Card>

        <Card className="hover:shadow-md transition-all duration-200 cursor-pointer group">
          <Link 
            to="/orders"
            className="block h-full focus:outline-none focus:ring-2 focus:ring-primary rounded-lg touch-manipulation"
            aria-label={t('orders')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Package className="h-7 w-7 sm:h-8 sm:w-8 text-primary group-hover:scale-110 transition-transform" aria-hidden="true" />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <CardTitle className="text-base sm:text-lg mb-1 sm:mb-2">{t('orders')}</CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {t('track_progress')}
              </CardDescription>
            </CardContent>
          </Link>
        </Card>

        <Card className="hover:shadow-md transition-all duration-200 cursor-pointer group">
          <Link 
            to="/help"
            className="block h-full focus:outline-none focus:ring-2 focus:ring-primary rounded-lg touch-manipulation"
            aria-label={t('help')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <FileText className="h-7 w-7 sm:h-8 sm:w-8 text-primary group-hover:scale-110 transition-transform" aria-hidden="true" />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <CardTitle className="text-base sm:text-lg mb-1 sm:mb-2">{t('help')}</CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {t('guides_tips')}
              </CardDescription>
            </CardContent>
          </Link>
        </Card>

        <Card className="hover:shadow-md transition-all duration-200 cursor-pointer group">
          <Link 
            to="/wishlist"
            className="block h-full focus:outline-none focus:ring-2 focus:ring-primary rounded-lg touch-manipulation"
            aria-label={`${t('wishlist')} - ${t('coming_soon')}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Heart className="h-7 w-7 sm:h-8 sm:w-8 text-primary group-hover:scale-110 transition-transform" aria-hidden="true" />
                <Badge variant="secondary" className="text-xs">{t('coming_soon')}</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <CardTitle className="text-base sm:text-lg mb-1 sm:mb-2">{t('wishlist')}</CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {t('favorite_dresses')}
              </CardDescription>
            </CardContent>
          </Link>
        </Card>

        <Card className="hover:shadow-md transition-all duration-200 cursor-pointer group">
          <Link 
            to="/loyalty"
            className="block h-full focus:outline-none focus:ring-2 focus:ring-primary rounded-lg touch-manipulation"
            aria-label={`${t('loyalty')} - ${t('coming_soon')}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Star className="h-7 w-7 sm:h-8 sm:w-8 text-primary group-hover:scale-110 transition-transform" aria-hidden="true" />
                <Badge variant="secondary" className="text-xs">{t('coming_soon')}</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <CardTitle className="text-base sm:text-lg mb-1 sm:mb-2">{t('loyalty')}</CardTitle>
              <CardDescription className="text-sm leading-relaxed">
                {t('earn_points')}
              </CardDescription>
            </CardContent>
          </Link>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg sm:text-xl flex items-center space-x-2">
            <Clock className="h-5 w-5 text-primary" aria-hidden="true" />
            <span>{t('recent_activity')}</span>
          </CardTitle>
          <CardDescription className="text-sm leading-relaxed">
            {t('latest_interactions')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4" role="feed" aria-label={t('recent_activity')}>
            {loading ? (
              <div className="flex items-center space-x-4 text-sm" role="status" aria-live="polite">
                <div className="h-4 w-4 bg-muted animate-pulse rounded flex-shrink-0" />
                <span className="text-muted-foreground">{t('loading')}</span>
              </div>
            ) : recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => {
                const IconComponent = activity.icon;
                return (
                  <div key={index} className="flex items-start space-x-3 text-sm p-3 rounded-lg hover:bg-accent/50 transition-colors">
                    <IconComponent className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <div className="flex-1 space-y-1">
                      <p className="font-medium text-foreground">{activity.title}</p>
                      <p className="text-muted-foreground">{activity.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(activity.date).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center space-x-4 text-sm text-center py-8" role="status" aria-live="polite">
                <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                <span className="text-muted-foreground">{t('no_recent_activity')}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg sm:text-xl">{t('contact_us')}</CardTitle>
          <CardDescription className="text-sm leading-relaxed">
            {t('get_in_touch')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start space-x-2 text-sm">
              <MapPin className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span className="leading-relaxed">{t('visit_boutique')}</span>
            </div>
            <Button 
              asChild 
              variant="outline" 
              className="mt-4 w-full sm:w-auto touch-manipulation"
              aria-label={t('contact_us')}
            >
              <Link to="/help">
                <MessageSquare className="mr-2 h-4 w-4" aria-hidden="true" />
                {t('contact_us')}
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}